import { FilterOperatorType } from '../components/odata-filter/models/filter-operator-type';
import {
  FilterGroup,
  ODataFilter,
  ODataFilterValues,
  ODataSubFilter,
} from '../components/odata-filter/models/odata-filter';
import { FilterOperator, OPERATORS } from '../components/odata-filter/models/operators';
import { SearchValueType } from '../components/odata-filter/models/search-value-type';
import { ODataFilterBuilder } from './odata-filter-builder';

export class ChildFieldsFilterModel implements ODataFilter {
  targetFieldId = '';

  subFilter = new ODataSubFilter();
  constructor(
    public childFieldName: string,
    public displayName: string,
    public targetField: string,
    public valueType: SearchValueType,
    public values?: any[] | ODataFilterValues[],
    public secondFilter?: any,
    public filterGroup = FilterGroup.SystemChildFields
  ) {}
  buildFilter(
    searchValue: any,
    filterOperator: FilterOperator,
    subQueryFilters?: any[],
    buildForAutomation?: boolean
  ) {
    if (
      subQueryFilters &&
      subQueryFilters.length > 0 &&
      this.valueType != SearchValueType.MultiValue
    ) {
      throw new Error(
        'subQueryFilters does not apply to any SearchValueType other than MultiValue'
      );
    }

    const targetField = filterOperator.targetField ?? this.targetField;
    const valueType = filterOperator.behaveAs ?? this.valueType;

    if (valueType == SearchValueType.String) {
      return this.buildStringTypeFilter(valueType, searchValue, filterOperator, targetField);
    } else if (valueType == SearchValueType.Number) {
      return this.buildNumberTypeFilter(valueType, searchValue, filterOperator, targetField);
    } else if (
      valueType == SearchValueType.MultiValue ||
      valueType == SearchValueType.WorkspaceMultiValue
    ) {
      return this.buildEnumeratedTypeFilter(
        searchValue,
        filterOperator,
        targetField,
        subQueryFilters
      );
    } else if (valueType == SearchValueType.Skill) {
      return this.buildSkillTypeFilter(searchValue, filterOperator, targetField);
    } else if (valueType == SearchValueType.Date) {
      return this.buildDateTypeFilter(
        valueType,
        searchValue,
        filterOperator,
        buildForAutomation,
        targetField
      );
    } else if (valueType == SearchValueType.List) {
      return this.buildListTypeFilter(valueType, searchValue, filterOperator, targetField);
    } else if (valueType == SearchValueType.Boolean) {
      return this.buildBooleanTypeFilter(valueType, searchValue, filterOperator, targetField);
    } else {
      throw new Error('Invalid Operation');
    }
  }

  private buildSkillTypeFilter(
    searchValue: ODataFilterValues[] | string,
    filterOperator: FilterOperator,
    targetField: string
  ) {
    if (
      filterOperator.displayOperator == FilterOperatorType.isEmpty ||
      filterOperator.displayOperator == FilterOperatorType.isNotEmpty
    ) {
      return this.buildEnumeratedTypeFilter([], filterOperator, targetField);
    } else if (searchValue && Array.isArray(searchValue)) {
      return this.buildEnumeratedTypeFilter(searchValue, filterOperator, targetField);
    } else {
      let _searchValue = searchValue ? (searchValue as string) : '';
      if (filterOperator.displayOperator == FilterOperatorType.contains) {
        return {
          [this.childFieldName]: {
            any: [
              ODataFilterBuilder.buildStringFilter(
                'name',
                _searchValue,
                FilterOperatorType.contains
              ),
              this.secondFilter ?? null,
            ],
          },
        };
      } else throw new Error('Invalid Operation');
    }
  }

  private buildStringTypeFilter(
    valueType: SearchValueType,
    searchValue: any,
    filterOperator: FilterOperator,
    targetField: string
  ) {
    let operatorTypes = OPERATORS[valueType];
    if (
      operatorTypes.operators.findIndex(
        (x) => x.displayOperator == filterOperator.displayOperator
      ) == -1
    ) {
      throw new Error('Invalid Operation');
    }
    let _searchValue =
      filterOperator.displayOperator == FilterOperatorType.isEmpty ||
      filterOperator.displayOperator == FilterOperatorType.isNotEmpty
        ? null
        : searchValue;

    let filter = {
      [this.childFieldName]: {
        any: [
          ODataFilterBuilder.buildStringFilter(
            targetField,
            _searchValue,
            filterOperator.actualOperator
          ),
          this.secondFilter ?? null,
        ],
      },
    };

    if (filterOperator.displayOperator == FilterOperatorType.notContains) return { not: filter };
    return filter;
  }
  private buildListTypeFilter(
    valueType: SearchValueType,
    searchValue: any,
    filterOperator: FilterOperator,
    targetField: string
  ) {
    let operatorTypes = OPERATORS[valueType];
    if (
      operatorTypes.operators.findIndex(
        (x) => x.displayOperator == filterOperator.displayOperator
      ) == -1
    ) {
      throw new Error('Invalid Operation');
    }

    let filter = {
      [this.childFieldName]: {
        any: [
          ODataFilterBuilder.buildSimpleFilter(
            targetField,
            searchValue,
            filterOperator.actualOperator
          ),
          this.secondFilter ?? null,
        ],
      },
    };

    if (filterOperator.displayOperator == FilterOperatorType.notContains) return { not: filter };
    return filter;
  }

  private buildBooleanTypeFilter(
    valueType: SearchValueType,
    searchValue: boolean,
    filterOperator: FilterOperator,
    targetField: string
  ) {
    let operatorTypes = OPERATORS[valueType];
    if (
      operatorTypes.operators.findIndex(
        (x) => x.displayOperator == filterOperator.displayOperator
      ) == -1
    ) {
      throw new Error('Invalid Operation');
    }

    let filter = {
      [this.childFieldName]: {
        any: [
          ODataFilterBuilder.buildSimpleFilter(
            targetField,
            searchValue,
            filterOperator.actualOperator
          ),
          this.secondFilter ?? null,
        ],
      },
    };

    if (filterOperator.displayOperator == FilterOperatorType.notContains) return { not: filter };
    return filter;
  }

  private buildNumberTypeFilter(
    valueType: SearchValueType,
    searchValue: any,
    filterOperator: FilterOperator,
    targetField: string
  ) {
    let operatorTypes = OPERATORS[valueType];

    if (
      operatorTypes.operators.findIndex(
        (x) => x.displayOperator == filterOperator.displayOperator
      ) == -1
    ) {
      throw new Error('Invalid Operation');
    }

    let _searchValue =
      filterOperator.displayOperator == FilterOperatorType.isEmpty ||
      filterOperator.displayOperator == FilterOperatorType.isNotEmpty
        ? null
        : searchValue;

    let filter = {
      [this.childFieldName]: {
        any: [
          ODataFilterBuilder.buildNumberFilter(
            targetField,
            _searchValue,
            filterOperator.actualOperator
          ),
          this.secondFilter ?? null,
        ],
      },
    };
    return filter;
  }

  private buildEnumeratedTypeFilter(
    searchValue: ODataFilterValues[],
    filterOperator: FilterOperator,
    targetField: string,
    subQueryFilters?: any[]
  ) {
    let searchListValue: any;
    if (!searchValue) {
      searchListValue = '';
    } else {
      if (searchValue.find((x) => x.searchValue)) {
        searchListValue = searchValue.map((x) => x.searchValue);
      } else {
        searchListValue = searchValue.map((x) => x.name);
      }
    }

    let notFilters = subQueryFilters?.filter((x) => x.not).map((x) => x.not);
    let noneNotFilters = subQueryFilters?.filter((x) => !x.not);

    if (filterOperator.displayOperator == FilterOperatorType.in) {
      if (subQueryFilters && subQueryFilters.length > 0) {
        return {
          [this.childFieldName]: {
            any: [
              {
                and: [
                  ODataFilterBuilder.buildArrayFilter(targetField, searchListValue),
                  notFilters?.length != 0 ? { not: notFilters } : null,
                  noneNotFilters?.length != 0 ? noneNotFilters : null,
                  this.secondFilter ?? null,
                ],
              },
            ],
          },
        };
      } else {
        return {
          [this.childFieldName]: {
            any: [
              ODataFilterBuilder.buildArrayFilter(targetField, searchListValue),
              this.secondFilter ?? null,
            ],
          },
        };
      }
    } else if (filterOperator.displayOperator == FilterOperatorType.notIn) {
      if (subQueryFilters && subQueryFilters.length > 0) {
        let _searchQuery = searchListValue.map((x) =>
          ODataFilterBuilder.buildGuidFilter(targetField, x, FilterOperatorType.ne)
        );
        _searchQuery = _searchQuery.concat(noneNotFilters, { not: notFilters });
        if (this.secondFilter) {
          _searchQuery.push(this.secondFilter);
        }
        return {
          [this.childFieldName]: {
            any: [{ and: _searchQuery }],
          },
        };
      } else {
        return {
          not: {
            [this.childFieldName]: {
              any: [
                ODataFilterBuilder.buildArrayFilter(targetField, searchListValue),
                this.secondFilter ?? null,
              ],
            },
          },
        };
      }
    } else if (filterOperator.displayOperator == FilterOperatorType.isEmpty) {
      return {
        not: {
          [this.childFieldName]: {
            any: [this.secondFilter ?? {}],
          },
        },
      };
    } else if (filterOperator.displayOperator == FilterOperatorType.isNotEmpty) {
      if (notFilters && notFilters.length != 0) {
        return {
          and: [
            {
              [this.childFieldName]: {
                any: [
                  noneNotFilters?.length != 0 ? { and: noneNotFilters } : null,
                  this.secondFilter ?? {},
                ],
              },
            },
            notFilters.map((notFilter) => ({
              not: {
                [this.childFieldName]: {
                  any: [notFilter],
                },
              },
            })),
          ],
        };
      } else {
        return {
          [this.childFieldName]: {
            any: [
              noneNotFilters?.length != 0 ? { and: noneNotFilters } : null,
              this.secondFilter ?? {},
            ],
          },
        };
      }
    } else throw new Error('Invalid Operation');
  }

  private buildDateTypeFilter(
    valueType: SearchValueType,
    searchValue: any,
    filterOperator: FilterOperator,
    buildForAutomation: boolean,
    targetField: string
  ) {
    let operatorTypes = OPERATORS[valueType];

    if (
      operatorTypes.operators.findIndex(
        (x) => x.displayOperator == filterOperator.displayOperator
      ) == -1
    ) {
      throw new Error('Invalid Operation');
    }
    let filter: { [k: string]: any } = {};

    filter[this.childFieldName] = {
      any: [
        ODataFilterBuilder.buildDateFilter(
          targetField,
          searchValue,
          filterOperator.displayOperator,
          false,
          buildForAutomation
        ),
        this.secondFilter ?? null,
      ],
    };
    return filter;
  }
}
